import React, { FC, ReactNode, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { DocumentNode } from 'graphql';
import { useIntl } from 'react-intl';

import MaterialTable from 'material-table';
import { Paper, Link, MenuItem, Select, TablePagination, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CSSProperties } from '@material-ui/core/styles/withStyles';

import DateCell from '../common/DateCell';
import commonMessages from '../common/messages';

import { useLanguageContext } from '../languages';
import { Highlight, HighlightType, HighlightFilterInput } from '../../utils/types';
import { getLocalTimezone, toLocalFormattedString } from '../../utils/dateTransform';

const useStyles = makeStyles((theme) => ({
	select: {
		minWidth: '140px'
	},
	noHighlights: {
		margin: theme.spacing(2)
	}
}));

const AUTO_REFETCH_PERIOD = 15 * 1000; // 15 seconds in milliseconds

const Highlights: FC<HighlightsProps> = (props) => {
	const { id, query, queryPath, hideFilter } = props;

	const intl = useIntl();
	const { tableLocalization } = useLanguageContext();

	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [filter, setFilter] = useState<HighlightFilterInput | undefined>(undefined);
	const [highlightType, setHighlightType] = useState('');

	const pageOptions = [5, 10, 20, 50];

	const handleFilterChange = (typeFilter: string): void => {
		setHighlightType(typeFilter);
		const filter = typeFilter ? { type: { eq: typeFilter } } : undefined;
		setFilter(filter);
		setPage(0);
		refetch({
			id: decodeURIComponent(id),
			limit: 1000,
			nextToken: null,
			filter: filter
		});
	};

	const classes = useStyles();

	const { loading, error, data, refetch } = useQuery(query, {
		variables: {
			id: decodeURIComponent(id),
			limit: 1000,
			nextToken: null,
			filter
		},
		pollInterval: AUTO_REFETCH_PERIOD
	});

	if (loading) return <Typography>Loading...</Typography>;
	if (error) return <Typography>Error loading data from the server</Typography>;
	if (!data[queryPath] || (data[queryPath].highlights.items.length < 1 && !data[queryPath].highlights.nextToken))
		return <Typography className={classes.noHighlights}>No highlights found</Typography>;

	const nextToken = data[queryPath]?.highlights.nextToken;

	const rowStyle = (rowData: Highlight): CSSProperties =>
		rowData.type === HighlightType.DRAFT || rowData.type === HighlightType.AUTOMATED ? { opacity: 0.3 } : {};

	const currentPage = data[queryPath]?.highlights.items.slice(page * pageSize, page * pageSize + pageSize);

	const HighlightTypeFilter = (): JSX.Element => {
		return (
			<Select
				labelId='type-label'
				id='type'
				label='TYPE'
				name='type'
				defaultValue={highlightType}
				onChange={(e): void => {
					handleFilterChange(e?.target?.value as string);
				}}
				className={classes.select}>
				<MenuItem value=''>
					<em>None</em>
				</MenuItem>
				<MenuItem value='DRAFT'>DRAFT</MenuItem>
				<MenuItem value='AUTOMATED'>AUTOMATED</MenuItem>
				<MenuItem value='LIVE'>LIVE</MenuItem>
			</Select>
		);
	};

	const handlePageChange = (page: number): void => {
		setPage(page);
	};

	const handlePageSizeChange = (pageSize: number): void => {
		setPageSize(pageSize);
		setPage(0);
		refetch({
			id: decodeURIComponent(id),
			limit: pageSize * 2,
			nextToken: null,
			filter
		});
	};

	return (
		<div style={{ maxWidth: '100%' }}>
			<MaterialTable
				localization={tableLocalization}
				columns={[
					{
						title: 'ID',
						field: 'id',
						filtering: false,
						render: (row: Highlight): JSX.Element => (
							<Link component={RouterLink} to={`/highlights/${row.id}`} color='secondary'>
								{row.id}
							</Link>
						)
					},
					{
						title: 'Clips',
						field: 'periods',
						filtering: false,
						render: (row: Highlight): number => row.periods?.items?.length || 0
					},
					{
						title: `Available time (${getLocalTimezone()})`,
						field: 'availableTime',
						filtering: false,
						render: (row: Highlight): JSX.Element => (
							<DateCell
								value={row.availableTime}
								showInvalid={`Unavailable (Created Time: ${toLocalFormattedString(row.publishTime!, true, false)})`}
								showTimezone={false}
							/>
						)
					},
					{
						title: 'Type',
						field: 'type',
						filtering: true,
						defaultFilter: highlightType,
						filterComponent: (props): JSX.Element => (hideFilter ? <></> : <HighlightTypeFilter />),
						render: (row: Highlight): string => (row.type ? row.type : '')
					}
				]}
				data={currentPage}
				options={{
					pageSize,
					search: false,
					sorting: false,
					filtering: !hideFilter,
					rowStyle,
					emptyRowsWhenPaging: false
				}}
				title='Highlights'
				components={{
					Container: (props): JSX.Element => <Paper {...props} data-testid='highlights-table' />,
					Pagination: ({ style }): JSX.Element => (
						<TablePagination
							style={style}
							rowsPerPage={pageSize}
							rowsPerPageOptions={pageOptions}
							count={nextToken ? -1 : data[queryPath]?.highlights.items.length}
							onRowsPerPageChange={(e): void => handlePageSizeChange(Number(e.target.value))}
							page={page}
							onPageChange={(e, page): void => handlePageChange(page)}
							labelDisplayedRows={({ from, to }): ReactNode =>
								intl.formatMessage(
									{
										...commonMessages.paginationLabel
									},
									{ from, to }
								)
							}
						/>
					)
				}}
			/>
		</div>
	);
};

type HighlightsProps = {
	id: string;
	query: DocumentNode;
	queryPath: string;
	hideFilter?: boolean;
};

export default Highlights;
